import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { CircleAlert, Info, type LucideIcon, TriangleAlert } from 'lucide-react';

import { cn } from '../lib/utils';

// Alignment and glyph gap per tone: shared by the note and, with an action, by its glyph-and-text block.
const TONE_LAYOUT = {
  info: 'items-center gap-2',
  warning: 'items-start gap-2',
  error: 'items-start gap-1.5',
} as const;

// The prototype's notes: info = infobox / bc-note, warning = warnbox, error = errbox (5px radius, regular weight).
// Padding follows the tone at the default size (12 / 8 / 8) and is 8 at sm, the in-row bc-msg.
const noticeVariants = cva('flex rounded-md border font-normal', {
  variants: {
    tone: {
      info: `${TONE_LAYOUT.info} border-neutral-100 bg-neutral-50 text-neutral-700`,
      warning: `${TONE_LAYOUT.warning} border-[#fddbba] bg-[#fef6ee] text-neutral-700`,
      error: `${TONE_LAYOUT.error} border-[#eaeaea] bg-[#fdf5fa] text-neutral-800`,
    },
    size: {
      default: 'text-sm leading-5',
      sm: 'p-2 text-[13px] leading-[18px]',
    },
  },
  compoundVariants: [
    { tone: 'info', size: 'default', className: 'p-3' },
    { tone: ['warning', 'error'], size: 'default', className: 'p-2' },
  ],
  defaultVariants: { tone: 'info', size: 'default' },
});

type Tone = NonNullable<VariantProps<typeof noticeVariants>['tone']>;

// `nudge` lowers the glyph onto the first text line; the info tone centres it instead.
const TONE_GLYPH: Record<Tone, { icon: LucideIcon; className: string; size: string; nudge: string }> = {
  info: { icon: Info, className: 'text-neutral-500', size: 'size-4', nudge: '' },
  warning: { icon: TriangleAlert, className: 'text-orange-400', size: 'size-[18px]', nudge: 'mt-px' },
  error: { icon: CircleAlert, className: 'text-[#ae1717]', size: 'size-4', nudge: 'mt-0.5' },
};
const SMALL_GLYPH = 'size-3.5';

// With an action, glyph and text are one block (its basis never exceeds the line: 10rem of text, the glyph and its
// gap), so the glyph keeps to the first line however the text wraps, and the action centres against the block or
// wraps under it on a phone (n-r10A-4, n-r11A-4). The block and the action sit 12px apart.
const ACTION_ROW = 'flex-wrap items-center gap-3';
const MESSAGE = 'flex min-w-0 max-w-max flex-1 basis-[min(calc(10rem_+_28px),100%)]';

export type NoticeProps = React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof noticeVariants> & {
    // Replaces the tone's glyph (e.g. Clock, Lock); it keeps the tone's colour.
    icon?: LucideIcon;
    // A control after the text, e.g. Retry.
    action?: React.ReactNode;
  };

// An inline banner. Roles and live regions stay with the caller: it adds none, so nothing is newly announced.
const Notice = React.forwardRef<HTMLDivElement, NoticeProps>(
  ({ tone, size, icon, action, className, children, ...props }, ref) => {
    const toneKey = tone ?? 'info';
    const glyph = TONE_GLYPH[toneKey];
    const Glyph = icon ?? glyph.icon;
    const glyphNode = (
      <Glyph
        aria-hidden="true"
        className={cn('shrink-0', size === 'sm' ? SMALL_GLYPH : glyph.size, glyph.nudge, glyph.className)}
      />
    );
    if (action == null)
      return (
        <div ref={ref} className={cn(noticeVariants({ tone, size }), className)} {...props}>
          {glyphNode}
          {children}
        </div>
      );
    return (
      <div ref={ref} className={cn(noticeVariants({ tone, size }), ACTION_ROW, className)} {...props}>
        <div className={cn(MESSAGE, TONE_LAYOUT[toneKey])}>
          {glyphNode}
          <span className="min-w-0">{children}</span>
        </div>
        {action}
      </div>
    );
  },
);
Notice.displayName = 'Notice';

export { Notice, noticeVariants };
